import type { ColumnDef, Getter, Row } from "@tanstack/react-table";
import type { AxiosError } from "axios";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import { StringParam, useQueryParams } from "use-query-params";
import {
  EditButton,
  ButtonWithConfirmDialog,
  DeleteButton,
  PrimaryButtonWithPlusIcon,
} from "../../../../components/Buttons/Buttons";
import { TablePageLayout } from "../../../../components/TablePage/TablePageLayout";
import { useNotifications } from "../../../../components/Notifications/NotificationsContext";
import { SlideOut } from "../../../../components/SlideOut/SlideOut";
import { CustomTooltip } from "../../../../components/Table/Table";
// import { endpoints } from "../../../../endpoints";
// import { ErrorHandler } from "../../../../ErrorHandler";
import type { IPagination } from "../../../../types/types";
import { useDebounce } from "../../../../util/hooks";
import {
  convertUserToOption,
  defaultHandleSort,
  makeUrlWithParams,
  toTitleCase,
  useStoreState,
} from "../../../../util/util";
import axios from "axios";
import { AddOrEditRegion } from "./AddOrEditRegion";
import { useCountriesList } from "../../../../util/Locations";
import { endpoints } from "../../../../endpoints";
import { getRegionsLocation, Region } from "./util";
import { Cell, SortingRule } from "react-table";
import { spacingTokens } from "../../../../designTokens";

// Types
type RegionsAPIResponse = {
  data: Region[];
  pagination: IPagination;
};

type TableData = Omit<Region, "locations"> & {
  countries: string[];
  states: string[];
};

export interface ManageRegionsProps {
  regions?: Region[];
  onAddRegion?: () => void;
  onEditRegion?: (region: Region) => void;
  onDeleteRegion?: (regionId: string) => void;
  onSearch?: (query: string) => void;
}

/**
 * The "Manage > Regions" page component.
 * Used for managing regions with search, add, edit, and delete functionality.
 */
export function ManageRegions(): JSX.Element {
  const [query, setQuery] = useQueryParams({
    q: StringParam,
  });
  const { t } = useTranslation();

  const [searchQuery, setSearchQuery] = useState(query.q || "");
  const [debouncedSearchQuery] = useDebounce(searchQuery, 1000);
  const [selectedRegion, setSelectedRegion] = useState<Region>();
  const [tableData, setTableData] = useState<TableData[]>([]);
  const [offset, setOffset] = useState(0);
  const [perPage] = useState(10);

  const [tablePagination, setTablePagination] = useState({
    perPage: perPage,
    pageCount: 0,
    pageIndex: 0,
  });

  const [showEditSlideOut, setEditSlideOut] = useState(false);

  const [sortingRules, setSortingRules] = useState<{
    sortBy?: string;
    orderBy: "asc" | "desc";
  }>({
    orderBy: "asc",
  });

  const { notifyError, notifySuccess } = useNotifications();
  const { storefront_id } = useStoreState();
  const countries = useCountriesList();

  const {
    data: regionsResponse,
    error: regionsError,
    mutate,
  } = useSWR<RegionsAPIResponse, AxiosError>(
    makeUrlWithParams(
      // Replace with actual endpoint when available
      endpoints.v2_storefronts_id_custom_regions(storefront_id),
      {
        offset: offset,
        limit: perPage,
        ...(sortingRules.sortBy && {
          sort_by: sortingRules.sortBy,
          order_by: sortingRules.orderBy ?? "asc",
        }),
        ...(debouncedSearchQuery && { q: debouncedSearchQuery }),
      }
    ),
    {
      fallbackData: {
        data: [],
        pagination: {
          total: 0,
          offset: 0,
          limit: perPage,
        },
      },
    } as any
  );

  const isLoading = !regionsResponse && !regionsError;

  const handleSort = async (rules: SortingRule<object>[]) =>
    defaultHandleSort(rules, sortingRules, setSortingRules, setTableData);

  const renderList = useCallback(
    (
      items: any[],
      keyFn: (item: any, idx: number) => string | number,
      labelFn: (item: any) => React.ReactNode
    ) => (
      <ul style={{ margin: 0, padding: 0 }}>
        {items.map((item, idx) => (
          <li key={keyFn(item, idx)}>{labelFn(item)}</li>
        ))}
      </ul>
    ),
    []
  );

  const lead_subscribers = useCallback(
    (subscribers: Region["lead_subscribers"]) => {
      const subscriber_name = (subscriber: {
        firstname: string;
        lastname: string;
      }) => `${subscriber.firstname} ${subscriber.lastname}`;
      const allSubscribers = subscribers ?? [];
      if (allSubscribers.length <= 2) {
        return allSubscribers.length
          ? allSubscribers.map(subscriber_name).join(", ")
          : "--";
      }
      const firstTwo = allSubscribers
        .slice(0, 2)
        .map(subscriber_name)
        .join(", ");
      return `${firstTwo} +${allSubscribers.length - 2}`;
    },
    []
  );

  const modify_string_list = (names: string[]) => {
    if (names.length <= 2) {
      return names.length ? names.join(", ") : "--";
    }
    const first_2 = names.slice(0, 2).join(", ");
    return `${first_2} +${names.length - 2}`;
  };

  const handleEdit = useCallback((table_data: TableData, regions: Region[]) => {
    const region = regions.find((region) => region.id === table_data.id);
    if (region) {
      setSelectedRegion(region);
      setEditSlideOut(true);
    }
  }, []);

  const handleDelete = useCallback(
    async (id: string) => {
      try {
        await axios.delete(
          endpoints.v2_storefronts_id_custom_regions_id(storefront_id, id)
        );
        await mutate();
        notifySuccess(t("Region deleted successfully"));
      } catch (error) {
        const errorMessage = (error as AxiosError)?.response?.data?.message;
        notifyError(
          errorMessage
            ? errorMessage
            : t("There was an error deleting the region"),
          { error }
        );
      }
    },
    [mutate, notifyError, notifySuccess, storefront_id, t]
  );

  const tableColumns = React.useMemo<ColumnDef<TableData>[]>(
    () => [
      {
        header: t("Region"),
        accessorKey: "custom_region",
      },
      {
        header: t("Country"),
        accessorKey: "countries",
        enableSorting: false,
        cell: ({
          getValue,
          row: { original },
        }: {
          getValue: Getter<TableData["countries"]>;
          row: Row<TableData>;
        }) => {
          const regionCountries = getValue();
          const countries = original.is_global
            ? regionCountries
            : regionCountries.slice(0, 1);
          return (
            <>
              <span data-tip data-for={`row-tooltip-countries-${original.id}`}>
                {modify_string_list(countries)}
              </span>
              {countries.length > 2 && (
                <CustomTooltip
                  id={`row-tooltip-countries-${original.id}`}
                  effect="solid"
                  backgroundColor="#60676f"
                  multiline={true}
                >
                  {renderList(
                    countries,
                    (country) => country,
                    (country) => country
                  )}
                </CustomTooltip>
              )}
            </>
          );
        },
      },
      {
        header: t("State"),
        accessorKey: "states",
        enableSorting: false,
        cell: ({
          getValue,
          row: { original },
        }: {
          getValue: Getter<TableData["states"]>;
          row: Row<TableData>;
        }) => {
          const states = getValue();
          return (
            <>
              <span data-tip data-for={`row-tooltip-states-${original.id}`}>
                {modify_string_list(states)}
              </span>
              {states.length > 2 && (
                <CustomTooltip
                  id={`row-tooltip-states-${original.id}`}
                  effect="solid"
                  backgroundColor="#60676f"
                  multiline={true}
                >
                  {renderList(
                    states,
                    (state) => state,
                    (state) => state
                  )}
                </CustomTooltip>
              )}
            </>
          );
        },
      },
      {
        header: t("Primary User"),
        accessorKey: "lead_assignee",
        cell: ({ getValue }: { getValue: Getter<Region["lead_assignee"]> }) => {
          return (
            <>{`${toTitleCase(getValue().firstname)} ${toTitleCase(
              getValue().lastname
            )}`}</>
          );
        },
      },
      {
        header: t("Additional Users"),
        accessorKey: "lead_subscribers",
        enableSorting: false,
        cell: ({
          getValue,
          row: { original },
        }: {
          getValue: Getter<Region["lead_subscribers"]>;
          row: Row<TableData>;
        }) => {
          const subscribers = getValue();
          return (
            <>
              <span
                data-tip
                data-for={`row-tooltip-lead-subscribers-${original.id}`}
              >
                {lead_subscribers(subscribers)}
              </span>
              {subscribers.length > 2 && (
                <CustomTooltip
                  id={`row-tooltip-lead-subscribers-${original.id}`}
                  effect="solid"
                  backgroundColor="#60676f"
                  multiline={true}
                >
                  {renderList(
                    subscribers,
                    (subscriber) => subscriber.firstname + subscriber.lastname,
                    (subscriber) => convertUserToOption(subscriber).label
                  )}
                </CustomTooltip>
              )}
            </>
          );
        },
      },
      {
        header: "",
        accessorKey: " ",
        cell: ({ row: { original } }: { row: Row<TableData> }) => {
          return (
            <>
              <div
                style={{
                  display: "flex",
                  gap: spacingTokens.spacing_xs,
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                <EditButton
                  testid={`edit-region-${original.id}`}
                  onClick={() =>
                    handleEdit(original, regionsResponse?.data ?? [])
                  }
                  width={16}
                  height={16}
                />
                <ButtonWithConfirmDialog
                  Button={(props) => (
                    <DeleteButton {...props} width={16} height={16} />
                  )}
                  testid={`delete-region-${original.id}`}
                  confirmMessage={t(
                    "Are you sure you want to delete this region?"
                  )}
                  handleConfirm={() => handleDelete(original.id)}
                  buttonText=""
                  useV2Buttons={true}
                />
              </div>
            </>
          );
        },
      },
    ],
    [
      t,
      renderList,
      lead_subscribers,
      handleEdit,
      regionsResponse?.data,
      handleDelete,
    ]
  );

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOffset(0);
    setSearchQuery(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setQuery({ q: undefined });
  };

  const changePage = (offset: number) => {
    setOffset(offset);
    setTableData([]);
  };

  const closeEditSlideOut = () => {
    setEditSlideOut(false);
    setSelectedRegion(undefined);
  };

  const openEditSlideOut = () => {
    setSelectedRegion(undefined);
    setEditSlideOut(true);
  };

  const handleSuccess = () => {
    setEditSlideOut(false);
    mutate();
  };

  const handleError = (error: unknown) => {
    console.error("Region operation failed:", error);
  };

  const rowHover = ({
    getValue,
    isTextOverflow,
    column: { id: cellId },
  }: Cell<TableData> & {
    getValue: Getter<TableData>;
    isTextOverflow: boolean;
    column: { id: string };
  }) => {
    const cellValue = getValue();

    if (typeof cellValue === "string" && isTextOverflow) {
      return cellValue;
    }

    if (Array.isArray(cellValue) && cellValue.length < 3 && isTextOverflow) {
      if (cellId === "lead_subscribers") {
        return renderList(
          cellValue,
          (subscriber) => subscriber.firstname + subscriber.lastname,
          (subscriber) => convertUserToOption(subscriber).label
        );
      }
      return renderList(
        cellValue,
        (state, idx) => state ?? idx,
        (state) => state
      );
    }

    return "";
  };

  useEffect(() => {
    const handleRegionsData = ({ data, pagination }: RegionsAPIResponse) => {
      setTableData(
        data.map(({ locations, ...rest }) => ({
          ...rest,
          countries: getRegionsLocation(locations, "country").map(
            ({ country }) => country
          ),
          states: rest.is_global
            ? []
            : getRegionsLocation(locations, "state").map(({ state }) => state),
        }))
      );
      setTablePagination({
        perPage: perPage,
        pageCount: Math.ceil(pagination.total / perPage),
        pageIndex: pagination.offset / perPage + 1,
      });
    };

    if (regionsResponse) {
      const { data: regionsData, pagination } = regionsResponse;
      handleRegionsData({ data: regionsData, pagination });
    }
  }, [regionsResponse, setTableData, perPage]);

  useEffect(() => {
    if (debouncedSearchQuery === "") setQuery({ q: undefined });
    if (debouncedSearchQuery) {
      setQuery({ q: debouncedSearchQuery });
    }
  }, [setQuery, query, debouncedSearchQuery]);

  return (
    <TablePageLayout<TableData>
      showTitle={false}
      searchQuery={searchQuery}
      searchPlaceholder={t("Search")}
      onSearchChange={handleSearch}
      onSearchClear={handleClearSearch}
      toolbarActions={
        <PrimaryButtonWithPlusIcon onClick={openEditSlideOut} useV2>
          {t("Add Region")}
        </PrimaryButtonWithPlusIcon>
      }
      columns={tableColumns}
      isLoading={isLoading}
      handleSort={handleSort}
      error={regionsError}
      data={tableData}
      rowHover={rowHover as any}
      pagination={tablePagination}
      offset={offset}
      onPageChange={changePage}
      sidePanel={
        <SlideOut
          closeFlyout={closeEditSlideOut}
          show={showEditSlideOut}
          title={
            selectedRegion ? t("Edit Region Mapping") : t("Add Region Mapping")
          }
        >
          <AddOrEditRegion
            region={selectedRegion}
            onSuccess={handleSuccess}
            onCancel={closeEditSlideOut}
            onError={handleError}
            countries={countries}
          />
        </SlideOut>
      }
    />
  );
}
